import {CallAudioDevices, CallPhoneCountry, CallPhoneInvite} from '@features/calls/call-media.port';
import {CallParticipantRow} from '@features/calls/call-participants';
import {CALL_ICONS} from '@shared/constants/call-tokens';

/**
 * How the compact call view is drawn (ITP-E-185-01, D-09/D-11): `card` — the stage, the thumbnails and the footer, as
 * Meet's picture-in-picture is — or `strip`, one slim row for a voice call or a minimized video call.
 */
export type CompactCallMode = 'card' | 'strip';

/** How large the host draws the card: the view pages its thumbnails and fills the room by it. */
export type CompactCallSize = 'small' | 'large' | 'full';

/** One person in the call's media as the view shows them: their video, else their initials, with a name and marks. */
export interface CompactCallPerson {
  /** Their identity in the session (the email the credential carried), lower-cased. */
  identity: string;
  /** As the view names them: "You" for this side. */
  name: string;
  initials: string;
  video: boolean;
  muted: boolean;
  speaking: boolean;
  self?: boolean;
}

/** This side's own controls, as the footer draws them. */
export interface CompactCallSelf {
  /** In the call's media: the controls work. */
  connected: boolean;
  muted: boolean;
  videoOn: boolean;
  sharing: boolean;
  /** Whether Share shows at all: not in a browser that cannot share a screen. */
  canShare: boolean;
}

/** Who shares their screen now. */
export interface CompactCallSharer {
  identity: string;
  name: string;
  /** This side shares: the stage says so instead of drawing its own screen back to it. */
  self: boolean;
}

/** What the options (⋯) menu may hold besides Participants and Devices; each is shown only when it is this side's to do. */
export interface CompactCallOptions {
  /** Open in Meet ↗, for anyone in the call; `meetReady` once the room is there to open. */
  openInMeet: boolean;
  meetReady: boolean;
  /** The host's Record (ITP-E-172-02); `recording` while it is requested or running, when the item is Stop recording (ITP-E-189-06). */
  record: boolean;
  recording: boolean;
  /**
   * Why Record is held back, when it is (ITP-E-198-04): a phone person is in the call, and Zoom announces no recording to
   * them. Record shows disabled, saying so.
   */
  recordHeldBack?: string | null;
  /** The host's End call for everyone, behind a confirm (ITP-E-182-04). */
  endForAll: boolean;
  /**
   * Add by phone (ITP-E-198-01): this user may add a phone person (OM, Calls:Enabled and Calls:PhoneEnabled) and the call's
   * media account dials out. Shown with {@link CompactCallViewModel.phone}.
   */
  addPhone?: boolean;
}

/** What Add by phone's dialog offers (ITP-E-198-01): only the countries the account dials. */
export interface CompactCallPhone {
  countries: CallPhoneCountry[];
}

/**
 * The microphones and speakers Devices offers, and the ones in use (ITP-E-189-12): what the media reports it runs on — the
 * host passes the media's answer through, never the click — marked with Meet's check and the tint.
 */
export interface CompactCallDevices {
  list: CallAudioDevices | null;
  microphoneId: string | null;
  speakerId: string | null;
}

/**
 * Everything the compact call view shows, built by whoever hosts it (the video call card, the call bar). The view is
 * presentational: it never reads the call service and never owns a media client.
 */
export interface CompactCallViewModel {
  mode: CompactCallMode;
  size: CompactCallSize;
  /** The call's name: the other person, or the group's title. */
  title: string;
  /** The timer, or what the call waits for. */
  timer: string;
  /** How many people are in the call (ITP-E-191-03): the strip's "mm:ss · N in call"; 0 says only the timer. */
  inCall: number;
  /** A video call: the strip shows the speaker's thumbnail. */
  video: boolean;
  self: CompactCallSelf;
  /** Everyone in the call's media, this side included. */
  people: CompactCallPerson[];
  /** Who the stage stands for — their video when on, else their initials; null shows the call's initials. */
  stage: CompactCallPerson | null;
  sharer: CompactCallSharer | null;
  /**
   * A group call's Participants with where they stand (CallParticipants); none in a 1:1 — until a phone person is added
   * (ITP-E-198-01), who is a row of their own, with Cancel or Hang up.
   */
  participants: CallParticipantRow[];
  options: CompactCallOptions;
  /** Add by phone's dialog, where `options.addPhone` (ITP-E-198-01). */
  phone?: CompactCallPhone | null;
  devices: CompactCallDevices;
  /** The red button's tooltip. */
  hangUpTitle: string;
  /** The strip offers Expand back to the card. */
  expandable: boolean;
}

/** The icons, Meet's PiP's: the one set both draw (ITP-E-185-02, `shared/constants/call-tokens.ts`). */
export const COMPACT_CALL_ICONS = CALL_ICONS;

/** What a choice in the options (⋯) menu asks the host to do. */
export type CompactCallOption =
  | {type: 'open-in-meet'}
  | {type: 'end-for-all'}
  | {type: 'record'}
  | {type: 'stop-recording'}
  | {type: 'devices'}
  | {type: 'microphone'; deviceId: string}
  | {type: 'speaker'; deviceId: string}
  /** Add by phone's Call (ITP-E-198-01). */
  | {type: 'add-phone'; invite: CallPhoneInvite}
  /** A phone person's Cancel or Hang up in Participants (ITP-E-198-01), by their phone key. */
  | {type: 'hang-up-phone'; key: string};

/**
 * Draws the call's media into the elements the view gives it. The host passes one in, so the view never owns a media
 * client — there is one per page, and it is the call's.
 */
export interface CompactCallRenderer {
  /** Draws `identity`'s video into `el`; false when they have none to draw. */
  attachVideo(identity: string, el: HTMLElement, size: 'small' | 'large'): Promise<boolean>;

  /** Drops the video it drew of `identity` into `el`. */
  detachVideo(identity: string, el: HTMLElement): Promise<void>;

  /**
   * Draws the screen `identity` shares into `el`, the stage's own canvas, as Meet draws it (ITP-E-189-05); false when it
   * cannot. The renderer keeps it drawn as the share stops and starts.
   */
  attachShare(el: HTMLElement, identity: string): Promise<boolean>;

  /** Stops drawing the screen into `el`. */
  detachShare(el: HTMLElement): Promise<void>;
}
